import React from 'react';
import type { Conversation, Participant } from '../types';
import StatusBadge from './StatusBadge';
import { useMessagesContext } from '../MessagesContext';

interface Props {
 conv: Conversation;
 participants: Participant[];
 onBack: () => void;
 actionPanel?: React.ReactNode;
 children: React.ReactNode;
}

/**
 * ConversationView 의 공통 chrome.
 * - 헤더 (뒤로 + status 배지 + 제목)
 * - 액션 패널 슬롯 (어댑터) — 맨 위. 메시지는 시간 역순(최신이 위)이라 폼이 최신 흐름에 바로 이어짐.
 * - 참여자 라인 (친화 이름, 클릭 가능)
 * - 메시지 thread 컨테이너 (시간 역순)
 */
const ThreadShell: React.FC<Props> = ({ conv, participants, onBack, actionPanel, children }) => {
 const { onPeerClick, onFamilyClick } = useMessagesContext();
 const active = participants.filter(p => !p.revoked_at);

 return (
  <div>
   <button onClick={onBack} className="text-blue-600 hover:underline text-sm mb-3">← 뒤로</button>

   <div className="mb-2">
    <StatusBadge kind={conv.kind} status={conv.status} />
   </div>
   <h1 className="text-xl font-bold mb-3">{conv.subject ?? '(제목 없음)'}</h1>

   {actionPanel}

   <div className="text-xs text-gray-500 mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
    <span>참여자:</span>
    {active.map((p, idx) => {
     const isFamily = !!p.family_hash;
     const label = isFamily
      ? (p.family_name || '패밀리')
      : (p.peer_name || p.peer_email || '사용자');
     return (
      <React.Fragment key={p.participant_id}>
       <button
        onClick={() => isFamily ? onFamilyClick(p.family_hash!) : onPeerClick(p)}
        className="text-blue-600 hover:underline"
        title={isFamily ? p.family_hash! : (p.peer_hash || p.acting_peer_hash || '')}
       >
        {label}
       </button>
       {idx < active.length - 1 && <span className="text-gray-300">·</span>}
      </React.Fragment>
     );
    })}
   </div>

   {/* 종류 태그를 레일 '위'에 중앙정렬로 얹음. ml=태그 좌측 절반, pl=태그 우측 절반+텍스트 들여쓰기. */}
   <ol className="relative border-l-2 border-gray-200 ml-12 md:ml-14 pl-8 py-2 list-none">
    {children}
   </ol>
  </div>
 );
};

export default ThreadShell;
